import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { getDashboardDenuncias } from '../../../../../app/modules/helpers/Api';
import { toolboxDownload } from '../charts/toolboxDownload';
import { eixoCategoria } from '../charts/eixosBase';

interface DadosDenuncias {
    por_status: {
        'Pending': number;
        'Checking': number;
        'Done': number;
        'Canceled': number;
        'Out of date': number;
    };
    prazo: { em_atraso: number; respondidas_fora_do_prazo: number };
    anonimas: { total: number; de: number };
    serie_mensal: { mes: string; total: number }[];
}

interface Props {
    empresaId: number;
}

// Grafia exata do ENUM redefinido pela migration 2023_04_01_113446, que e
// tambem a que os badges de CompanyRequeriments.tsx:309-312 usam.
type ChaveStatus = 'Pending' | 'Checking' | 'Done' | 'Canceled' | 'Out of date';

const ROTULO_STATUS: { chave: ChaveStatus; texto: string; classe: string }[] = [
    { chave: 'Pending', texto: 'Pendentes', classe: 'text-warning' },
    { chave: 'Checking', texto: 'Em andamento', classe: 'text-info' },
    { chave: 'Done', texto: 'Finalizadas', classe: 'text-success' },
    { chave: 'Out of date', texto: 'Fora do prazo', classe: 'text-danger' },
    { chave: 'Canceled', texto: 'Canceladas', classe: 'text-gray-600' },
];

const PainelDenuncias: React.FC<Props> = ({ empresaId }) => {
    const [dados, setDados] = useState<DadosDenuncias | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(false);

    const ref = React.useRef<any>(null);

    useEffect(() => {
        setErro(false);
        setCarregando(true);

        getDashboardDenuncias(empresaId)
            .then((response) => setDados(response.data.data))
            .catch(() => setErro(true))
            .finally(() => setCarregando(false));
    }, [empresaId]);

    const opcao = (serie: { mes: string; total: number }[]) => ({
        title: {
            text: 'Denúncias recebidas por mês',
            left: 'center',
            textStyle: { color: '#333', fontSize: 16 },
        },
        grid: { top: 60, bottom: 60 },
        tooltip: { trigger: 'axis' },
        xAxis: {
            ...eixoCategoria('', serie.map((ponto) => ponto.mes)),
            axisLabel: { fontSize: 11, interval: 0, rotate: 45 },
        },
        yAxis: {
            type: 'value',
            minInterval: 1,
            splitLine: { show: true },
            axisLabel: { fontSize: 12 },
        },
        series: [{
            type: 'line',
            smooth: false,
            data: serie.map((ponto) => ponto.total),
            itemStyle: { color: '#d9214e' },
            areaStyle: { color: 'rgba(217, 33, 78, 0.15)' },
        }],
        toolbox: toolboxDownload(ref, 'denuncias-por-mes'),
    });

    if (carregando) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                <span className='spinner-border spinner-border-lg align-middle'></span>
            </div>
        );
    }

    if (erro) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                <span className='text-gray-600 fw-bold fs-4'>Não foi possível carregar o canal de denúncia.</span>
            </div>
        );
    }

    if (!dados || dados.anonimas.de === 0) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                <span className='text-gray-600 fw-bold fs-4'>Esta empresa ainda não recebeu denúncias.</span>
            </div>
        );
    }

    return (
        <div className='col-12 mb-10'>
            <h3 className='mb-1'>Canal de denúncia</h3>
            <p className='text-gray-600 fw-bold fs-7'>{dados.anonimas.de} denúncia(s) recebida(s)</p>

            <div className='row mb-5'>
                {ROTULO_STATUS.map((item) => (
                    <div className='col-6 col-md-4 col-lg-2 mb-3' key={item.chave}>
                        <div className='bg-light rounded-3 p-4 text-center'>
                            <div className={`fw-bolder fs-1 ${item.classe}`}>{dados.por_status[item.chave]}</div>
                            <div className='text-gray-600 fw-bold fs-7'>{item.texto}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div className='row mb-5'>
                <div className='col-12 col-md-4'>
                    <div className='bg-light rounded-3 p-4 text-center'>
                        <div className='fw-bolder fs-1 text-danger'>{dados.prazo.em_atraso}</div>
                        <div className='text-gray-600 fw-bold fs-7'>Em atraso agora</div>
                    </div>
                </div>
                <div className='col-12 col-md-4'>
                    <div className='bg-light rounded-3 p-4 text-center'>
                        <div className='fw-bolder fs-1 text-danger'>{dados.prazo.respondidas_fora_do_prazo}</div>
                        <div className='text-gray-600 fw-bold fs-7'>Respondidas fora do prazo</div>
                    </div>
                </div>
                <div className='col-12 col-md-4'>
                    <div className='bg-light rounded-3 p-4 text-center'>
                        <div className='fw-bolder fs-1 text-dark'>
                            {dados.anonimas.total} <span className='fs-4 text-gray-600'>de {dados.anonimas.de}</span>
                        </div>
                        <div className='text-gray-600 fw-bold fs-7'>Anônimas</div>
                    </div>
                </div>
            </div>

            <ReactECharts
                ref={ref}
                option={opcao(dados.serie_mensal)}
                opts={{ renderer: 'svg' }}
                style={{ minHeight: '320px', width: '100%' }}
            />
        </div>
    );
};

export default PainelDenuncias;
